// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState } from 'react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import SearchableSelect from '../common/SearchableSelect'

// D-CRX-45 Clinical Review status + determination controls (CR-D3..D7). One implementation for
// the worklist row AND the detail screen: the detail screen had none, so a pharmacist who saved
// the care plan there had to go back to the list to record the outcome — and read the unchanged
// row as "my save didn't stick" (BioAccess ticket, Rx 140975, ClickUp 123qmzqzhv8).

export const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', in_progress: 'In progress', pending_callback: 'Pending call back',
  pending: 'Pending', cancelled: 'Cancelled',
}
export const DETERM_LABEL: Record<string, string> = { pending: 'Pending', approved: 'Approved', conditional: 'Conditional', denied: 'Denied' }

const STATUSES = ['draft', 'in_progress', 'pending_callback', 'pending', 'cancelled']
const DETERMS = ['pending', 'approved', 'conditional', 'denied']

const sel: React.CSSProperties = { fontSize: 12, padding: '3px 6px', border: '1px solid #dde5ec', borderRadius: 6, background: '#fff' }

/** onSaved fires after the server accepted the write; onError gets a user-facing message. */
type Callbacks = { onSaved: () => void; onError: (msg: string) => void }

export function CrStatusSelect({ therapyId, value, onSaved, onError }: { therapyId: string; value: string } & Callbacks) {
  return (
    <div style={{ minWidth: 130 }}><SearchableSelect value={value} ariaLabel="Status"
      onChange={async (v: string) => {
        try { await api.setClinicalReviewStatus(therapyId, v); onSaved() } catch (e) { onError(friendlyError(e)) }
      }}
      options={STATUSES.map(s => ({ value: s, label: STATUS_LABEL[s] }))} /></div>
  )
}

/**
 * Determination select. Denied asks for the reason first (CR-D7). Approved with required analytes
 * and no entered results is refused by the server (H-5); the audited attestation is offered inline
 * instead of leaking the raw API hint, then re-approves with approve_without_results.
 */
export function CrDeterminationControl({ therapyId, value, onSaved, onError }: { therapyId: string; value: string } & Callbacks) {
  const [mode, setMode] = useState<'select' | 'deny' | 'attest'>('select')
  const [text, setText] = useState('')
  const close = () => { setMode('select'); setText('') }

  const save = async (d: string, reason?: string, extras?: { approve_without_results?: boolean; no_results_reason?: string }) => {
    try { await api.setClinicalReviewDetermination(therapyId, d, reason, extras); close(); onSaved() }
    catch (err) {
      const b = (err as { body?: { error?: string } }).body
      if (d === 'approved' && !extras && b?.error === 'lab_results_missing') { setMode('attest'); setText(''); onError('') }
      else onError(friendlyError(err))
    }
  }

  if (mode === 'deny') return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
      <input value={text} onChange={e => setText(e.target.value)} placeholder="Denial reason (required)" style={{ ...sel, width: 180 }} />
      <button style={{ ...sel, background: '#dc2626', color: '#fff', border: 'none', cursor: 'pointer' }}
        onClick={() => { if (text.trim()) void save('denied', text) }}>Confirm</button>
      <button style={{ ...sel, cursor: 'pointer' }} onClick={close}>✕</button>
    </span>
  )
  if (mode === 'attest') return (
    <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 11, color: '#93610f', fontWeight: 600 }}>No lab results entered — attest to approve anyway (audited):</span>
      <input value={text} onChange={e => setText(e.target.value)} placeholder="Where were the results reviewed? (required)" style={{ ...sel, width: 220 }} />
      <button style={{ ...sel, background: '#185FA5', color: '#fff', border: 'none', cursor: 'pointer' }}
        onClick={() => { if (text.trim()) void save('approved', undefined, { approve_without_results: true, no_results_reason: text }) }}>Attest &amp; approve</button>
      <button style={{ ...sel, cursor: 'pointer' }} onClick={close}>✕</button>
    </span>
  )
  return (
    <div style={{ minWidth: 130 }}><SearchableSelect value={value} ariaLabel="Determination"
      onChange={(v: string) => { if (v === 'denied') { setMode('deny'); setText('') } else void save(v) }}
      options={DETERMS.map(d => ({ value: d, label: DETERM_LABEL[d] }))} /></div>
  )
}
